import { useState, type FormEvent } from "react";
import RatingStars from "@/components/base/RatingStars";
import { replyToReview, type OwnerReview, type OwnerReviewStatus } from "@/services/reviewService";
import { ApiError } from "@/services/http";

interface OwnerReviewCardProps {
  review:   OwnerReview;
  token:    string | null;
  onReplied: (id: number, reply: string) => void;
}

const STATUS_META: Record<OwnerReviewStatus, { label: string; className: string }> = {
  approved: { label: "Published",  className: "bg-emerald-100 text-emerald-700" },
  pending:  { label: "In review",  className: "bg-amber-100 text-amber-700" },
  rejected: { label: "Removed",    className: "bg-rose-100 text-rose-700" },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    year: "numeric", month: "long", day: "numeric",
  });
}

export default function OwnerReviewCard({ review, token, onReplied }: OwnerReviewCardProps) {
  const [showReply, setShowReply] = useState(false);
  const [reply,     setReply]     = useState("");
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState("");

  const meta = STATUS_META[review.status];

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!reply.trim()) { setError("Please write a reply before publishing."); return; }
    setSaving(true);
    setError("");
    try {
      await replyToReview(token, review.id, reply.trim());
      onReplied(review.id, reply.trim());
      setShowReply(false);
      setReply("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not publish your reply. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className={`px-6 py-5 ${review.flagCount > 0 ? "bg-rose-50/30" : ""}`}>
      <div className="flex flex-wrap items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
          {review.authorName.charAt(0).toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-bold text-foreground-950">{review.authorName}</p>
            <RatingStars rating={review.rating} showValue={false} />
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${meta.className}`}>
              {meta.label}
            </span>
            {review.flagCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-[10px] font-semibold text-rose-700">
                <i className="ri-flag-fill text-[10px]" />
                {review.flagCount} report{review.flagCount > 1 ? "s" : ""}
              </span>
            )}
          </div>

          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-store-3-line text-[12px]" />
              {review.businessName}
            </span>
            <span aria-hidden="true">·</span>
            <span>{formatDate(review.reviewedAt ?? review.createdAt)}</span>
          </p>

          <p className="mt-3 text-sm leading-relaxed text-foreground-700">
            {review.comment || "—"}
          </p>

          {/* Existing owner reply */}
          {review.ownerReply && (
            <div className="mt-3 rounded-lg border-l-2 border-primary-400 bg-primary-50/60 px-4 py-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary-700">
                <i className="ri-reply-line text-sm" /> Your reply
              </p>
              <p className="mt-1 text-sm leading-relaxed text-foreground-700">{review.ownerReply}</p>
              {review.repliedAt && (
                <p className="mt-1 text-[11px] text-foreground-400">{formatDate(review.repliedAt)}</p>
              )}
            </div>
          )}

          {/* Reply actions / form */}
          {!showReply && !review.ownerReply && (
            <button
              type="button"
              onClick={() => setShowReply(true)}
              className="mt-3 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              <i className="ri-reply-line text-sm" /> Reply
            </button>
          )}

          {showReply && (
            <form onSubmit={handleSubmit} className="mt-3">
              <textarea
                rows={3}
                maxLength={1000}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={`Write a public reply to ${review.authorName}…`}
                className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              {error && (
                <p className="mt-1.5 flex items-center gap-1.5 text-xs text-rose-600">
                  <i className="ri-error-warning-line text-sm" />{error}
                </p>
              )}
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => { setShowReply(false); setReply(""); setError(""); }}
                  className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-md border border-background-300 px-4 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving
                    ? <><i className="ri-loader-4-line animate-spin text-sm" /> Publishing…</>
                    : <><i className="ri-send-plane-line text-sm" /> Publish reply</>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </article>
  );
}